import { site } from "@/content/site";

export const SITE_URL = "https://leawpsychotherapy.com";
export const SITE_NAME = site.practitioner.fullTitle;
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;
export const PORTRAIT_IMAGE = `${SITE_URL}/images/lea-portrait-1000.webp`;

export type PageSeo = {
  title: string;
  description: string;
  path: string;
  ogType?: "website" | "article";
  noindex?: boolean;
  includeCanonical?: boolean;
};

const sameAs = [
  site.links.psychologyToday,
  site.links.linkedIn,
  site.links.brainspottingDirectory,
];

export const pageSeo = {
  home: {
    title: "Lea Walchshauser, LMFT — Psychotherapy in California",
    description:
      "Integrative psychotherapy with Lea Walchshauser, LMFT. Brainspotting, somatic, and systemic therapy for individuals, couples, and clinicians across California.",
    path: "/",
  },
  about: {
    title: "About Lea Walchshauser, LMFT — Integrative Therapy",
    description:
      "Learn about Lea Walchshauser's grounded, integrative approach to psychotherapy for individuals, couples, and clinicians across California via secure telehealth.",
    path: "/about",
  },
  workWith: {
    title: "Who I Work With — Therapy for Adults, Couples & Clinicians",
    description:
      "Individual therapy, couples counseling, and clinical supervision with Lea Walchshauser, LMFT. Trauma-informed care for adults and licensed clinicians in California.",
    path: "/who-i-work-with",
  },
  areasOfFocus: {
    title: "Areas of Focus — Trauma, Anxiety, Relationships & More",
    description:
      "Explore clinical focus areas including trauma, anxiety, depression, grief, relationship distress, life transitions, and clinician burnout with Lea Walchshauser, LMFT.",
    path: "/areas-of-focus",
  },
  modalities: {
    title: "Therapy Modalities — Brainspotting, Somatic & Systemic Work",
    description:
      "An integrative blend of Brainspotting, somatic, systemic, and solution-focused therapy tailored to your nervous system, relationships, and goals.",
    path: "/modalities",
  },
  brainspotting: {
    title: "Brainspotting Therapy — Trauma-Informed Treatment",
    description:
      "Brainspotting with Lea Walchshauser, LMFT: a brain-body approach for trauma, anxiety, and emotional regulation. Telehealth across California.",
    path: "/modalities/brainspotting",
  },
  supervision: {
    title: "Clinical Supervision for LMFT, LCSW & LPCC Trainees",
    description:
      "Reflective, trauma-informed clinical supervision for associate and pre-licensed clinicians. Individual and dyadic formats with Lea Walchshauser, LMFT.",
    path: "/supervision",
  },
  fees: {
    title: "Fees & Insurance — Session Rates & Headway Coverage",
    description:
      "Therapy and supervision fees, insurance through Headway, and payment details for individual, couples, and KAP sessions with Lea Walchshauser, LMFT.",
    path: "/fees",
  },
  contact: {
    title: "Contact — Schedule a Free Consultation",
    description:
      "Contact Lea Walchshauser, LMFT to schedule a free 15-minute consultation, ask about therapy or supervision, or verify insurance through Headway.",
    path: "/contact",
  },
  notFound: {
    title: "Page Not Found — Lea Walchshauser, LMFT",
    description: "The page you requested could not be found on leawpsychotherapy.com.",
    path: "/404",
    noindex: true,
    includeCanonical: false,
  },
} as const satisfies Record<string, PageSeo>;

const routeSeoMap: Record<string, PageSeo> = {
  "/": pageSeo.home,
  "/about": pageSeo.about,
  "/who-i-work-with": pageSeo.workWith,
  "/areas-of-focus": pageSeo.areasOfFocus,
  "/modalities": pageSeo.modalities,
  "/modalities/brainspotting": pageSeo.brainspotting,
  "/supervision": pageSeo.supervision,
  "/fees": pageSeo.fees,
  "/contact": pageSeo.contact,
};

export const sitemapPaths = Object.keys(routeSeoMap).filter((path) => path !== "/404");

export function buildCanonicalUrl(path: string): string {
  if (path === "/") {
    return `${SITE_URL}/`;
  }

  return `${SITE_URL}${path}`;
}

export function getSeoForPath(pathname: string): PageSeo {
  return routeSeoMap[pathname] ?? { ...pageSeo.notFound, path: pathname };
}

function breadcrumb(name: string, path: string, position: number) {
  return {
    "@type": "ListItem",
    position,
    name,
    item: buildCanonicalUrl(path),
  };
}

function buildBreadcrumbs(items: Array<{ name: string; path: string }>) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => breadcrumb(item.name, item.path, index + 1)),
  };
}

function buildWebPage(seo: PageSeo) {
  const url = buildCanonicalUrl(seo.path);

  return {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: seo.title,
    description: seo.description,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#person` },
  };
}

export function buildHomeJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: SITE_NAME,
        description: site.description,
        publisher: { "@id": `${SITE_URL}/#person` },
        inLanguage: "en-US",
      },
      {
        "@type": ["Person", "Physician"],
        "@id": `${SITE_URL}/#person`,
        name: site.practitioner.name,
        jobTitle: "Licensed Marriage and Family Therapist",
        url: `${SITE_URL}/`,
        image: PORTRAIT_IMAGE,
        description: site.description,
        sameAs,
        hasCredential: {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "license",
          name: "Licensed Marriage and Family Therapist",
          identifier: site.practitioner.licenseNumber,
        },
        knowsAbout: [
          "Trauma therapy",
          "Brainspotting",
          "Couples therapy",
          "Clinical supervision",
          "Anxiety treatment",
          "Somatic psychotherapy",
        ],
        areaServed: {
          "@type": "State",
          name: "California",
        },
      },
      {
        "@type": "ProfessionalService",
        "@id": `${SITE_URL}/#service`,
        name: `${site.practitioner.name} Psychotherapy`,
        url: `${SITE_URL}/`,
        image: DEFAULT_OG_IMAGE,
        description: site.description,
        provider: { "@id": `${SITE_URL}/#person` },
        areaServed: {
          "@type": "State",
          name: "California",
        },
        serviceType: [
          "Psychotherapy",
          "Couples therapy",
          "Clinical supervision",
          "Brainspotting",
        ],
        availableChannel: {
          "@type": "ServiceChannel",
          serviceUrl: buildCanonicalUrl("/contact"),
          serviceType: "Telehealth",
        },
      },
    ],
  };
}

export function getJsonLdForPath(pathname: string): Record<string, unknown> | null {
  if (pathname === "/") {
    return buildHomeJsonLd();
  }

  const seo = routeSeoMap[pathname];
  if (!seo) {
    return null;
  }

  const breadcrumbsByPath: Record<string, Array<{ name: string; path: string }>> = {
    "/about": [
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
    ],
    "/who-i-work-with": [
      { name: "Home", path: "/" },
      { name: "Who I Work With", path: "/who-i-work-with" },
    ],
    "/areas-of-focus": [
      { name: "Home", path: "/" },
      { name: "Areas of Focus", path: "/areas-of-focus" },
    ],
    "/modalities": [
      { name: "Home", path: "/" },
      { name: "Modalities", path: "/modalities" },
    ],
    "/modalities/brainspotting": [
      { name: "Home", path: "/" },
      { name: "Modalities", path: "/modalities" },
      { name: "Brainspotting", path: "/modalities/brainspotting" },
    ],
    "/supervision": [
      { name: "Home", path: "/" },
      { name: "Supervision", path: "/supervision" },
    ],
    "/fees": [
      { name: "Home", path: "/" },
      { name: "Fees & Insurance", path: "/fees" },
    ],
    "/contact": [
      { name: "Home", path: "/" },
      { name: "Contact", path: "/contact" },
    ],
  };

  const breadcrumbs = breadcrumbsByPath[pathname];
  if (!breadcrumbs) {
    return null;
  }

  const graph: Record<string, unknown>[] = [buildWebPage(seo), buildBreadcrumbs(breadcrumbs)];

  if (pathname === "/modalities/brainspotting") {
    graph.push({
      "@type": "MedicalTherapy",
      "@id": `${buildCanonicalUrl(seo.path)}#therapy`,
      name: "Brainspotting",
      description: seo.description,
      url: buildCanonicalUrl(seo.path),
      medicineSystem: "Psychotherapy",
      relevantSpecialty: {
        "@type": "MedicalSpecialty",
        name: "Psychotherapy",
      },
    });
  }

  if (pathname === "/supervision") {
    graph.push({
      "@type": "Service",
      "@id": `${buildCanonicalUrl(seo.path)}#service`,
      name: "Clinical Supervision",
      description: seo.description,
      url: buildCanonicalUrl(seo.path),
      provider: { "@id": `${SITE_URL}/#person` },
      areaServed: {
        "@type": "State",
        name: "California",
      },
      serviceType: "Clinical supervision",
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
